import {ExtraPrice, OrderLine} from '../../../transport/apiServiceResponse';
import {filterStickersByEligibility} from '../filterStickers';
import {PricingIntent, ResolvedPricing, UserPriceState} from '../types';

const isFiniteCents = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Extras are optional and often incomplete (null array, missing sell/normal,
 * loyalty-only, non-member-only). Treat a tier as usable only when the cents
 * fields we need are finite numbers — never invent prices from malformed rows.
 */
const loyaltySell = (extra: ExtraPrice | undefined): number | null =>
  extra && isFiniteCents(extra.sell_price) ? extra.sell_price : null;

export function resolvePricing(
  item: OrderLine,
  userState: UserPriceState,
  intent: PricingIntent,
): ResolvedPricing {
  const userPrice = item.sell_price_cents;
  const normalPrice = item.normal_price_cents;

  // `extra_prices: null` must not throw — destructuring defaults do not cover null.
  const extras = Array.isArray(item.extra_prices) ? item.extra_prices : [];

  const publicLoyalty = extras.find(
    p => p.price_level_external_reference === 'lmg_loyalty' && p.external_sell_type === 'public',
  );
  const privateLoyalty = extras.find(
    p => p.price_level_external_reference === 'lmg_loyalty' && p.external_sell_type === 'private',
  );
  const nonMemberExtra = extras.find(p => p.price_level_external_reference === 'non_member_price');

  const nonMemberSell =
    nonMemberExtra && isFiniteCents(nonMemberExtra.sell_price) ? nonMemberExtra.sell_price : null;
  const nonMemberNormal =
    nonMemberExtra && isFiniteCents(nonMemberExtra.normal_price)
      ? nonMemberExtra.normal_price
      : null;
  // Dual-pricing sell figure — null when the tier is absent or sell_price is unusable.
  const nonMemberPrice = nonMemberSell;

  const publicSell = loyaltySell(publicLoyalty);
  const privateSell = loyaltySell(privateLoyalty);

  const isPublicBetter =
    publicSell !== null && nonMemberPrice !== null && publicSell < nonMemberPrice;
  const isPrivateBetter =
    privateSell !== null && nonMemberPrice !== null && privateSell < nonMemberPrice;

  let memberPrice: number | null = null;
  let membershipType: 'public' | 'private' | null = null;

  if (isPrivateBetter) {
    memberPrice = privateSell;
    membershipType = 'private';
  } else if (isPublicBetter) {
    memberPrice = publicSell;
    membershipType = 'public';
  }

  const hasMemberPricing = memberPrice !== null;

  // DIS-1590: `memberEligible` decides LABELLING and whether a `private` (targeted)
  // tier may be shown at all. It does NOT decide the price — see below.
  const memberEligible =
    hasMemberPricing &&
    ((membershipType === 'public' && userState.publicPricing) ||
      (membershipType === 'private' && userState.privatePricing));

  // DIS-1590: `sell_price_cents` is SHOPPER-scoped — MFL resolves it against the
  // caller's session — so it is the authoritative transactional price and needs no
  // override from a client-side eligibility guess.
  //
  // Measured 2026-09-02, same endpoint, same store, equivalent pricing shape:
  //   no session      -> sell_price_cents 5399, extras: lmg_loyalty 5199 / non_member 5399
  //   member session  -> sell_price_cents 5199, extras: lmg_loyalty 5199 / non_member 5399
  // The guest was then charged `subtotal.cents_inc_tax` of 5399, matching their
  // `sell_price_cents`. Deriving the price from eligibility instead over-quoted every
  // logged-out shopper, and an eligibility flag that lags (rewards not yet loaded)
  // would under-quote a member. Trust MFL's figure.
  //
  // `marketing` keeps leading with the non-member price: tiles advertise the product,
  // they do not quote the reader.
  const displayPrice = intent === 'marketing' ? (nonMemberPrice ?? userPrice) : userPrice;

  // DIS-1590: Was is a PRODUCT fact (body), not a shopper saving (footer).
  // When a usable `non_member_price` tier exists, sale state comes from that tier's
  // normal vs sell so member / guest / logged-out bodies stay identical. Top-level
  // sell/normal remain shopper-scoped and drive the footer only. Extras are optional —
  // missing or non-numeric sell/normal → fall back to top-level comparison.
  const usableNonMemberTier = nonMemberSell !== null && nonMemberNormal !== null;
  let isOnSale: boolean;
  let saleSavings: number;
  let wasPrice: number | null;
  if (usableNonMemberTier) {
    isOnSale = nonMemberNormal > nonMemberSell;
    saleSavings = isOnSale ? nonMemberNormal - nonMemberSell : 0;
    wasPrice = isOnSale ? nonMemberNormal : null;
  } else {
    isOnSale = normalPrice > userPrice;
    saleSavings = isOnSale ? normalPrice - userPrice : 0;
    wasPrice = isOnSale ? normalPrice : null;
  }

  const memberSavings =
    hasMemberPricing && nonMemberPrice !== null ? nonMemberPrice - memberPrice! : 0;

  // DIS-1590: advertise a member price wherever the store can honour one. A private
  // tier is targeted, so it is advertised only to a shopper actually entitled to it.
  const advertiseMemberPrice =
    userState.storeLoyaltyEnabled &&
    hasMemberPricing &&
    (membershipType === 'public' || memberEligible);

  const showMemberLabel = memberEligible;
  // DIS-1590: this is the BADGE's copy, and the badge is an advert, so it follows
  // `advertiseMemberPrice`. It was previously gated on entitlement, which meant a
  // logged-out shopper got `showLabel` true but `labelText` null — and
  // `MembersPriceLabel` renders nothing without text, so the badge silently vanished
  // from every tile and carousel.
  const memberLabelText = advertiseMemberPrice
    ? membershipType === 'private'
      ? 'JUST FOR YOU'
      : 'MEMBERS PRICE'
    : null;

  const visibleStickers =
    intent === 'marketing'
      ? item.stickers || []
      : filterStickersByEligibility(item.stickers || [], userState, {
          presentAsPublicMemberProduct: isPublicBetter,
          presentAsPrivateMemberProduct: isPrivateBetter,
        });

  return {
    userPrice,
    normalPrice,
    memberPrice,
    nonMemberPrice,
    displayPrice,
    isOnSale,
    saleSavings,
    wasPrice,
    membershipType,
    hasMemberPricing,
    memberSavings,
    showMemberLabel,
    memberLabelText,
    visibleStickers,
    advertiseMemberPrice,
    intent,
  };
}
